import React, { useState, useEffect } from 'react';
import { withApollo } from 'react-apollo';
import { object, bool, array, string } from 'prop-types';
import { get, filter, isEmpty, find, toNumber, map, head, toString, last, omitBy } from 'lodash';
import moment from 'moment';
import { Upload, Button } from 'antd';
import * as Yup from 'yup';
import { Field, Formik, Form } from 'formik';
import withFirebase from '../../../hoc/withFirebase';
import {
    FlexContainer,
    Row,
    FormikInput,
    FormikSelect,
    Text,
    Checkbox,
    ConfirmFooterButtons,
    FormikRadio,
    FormikDatePicker,
    Icon
} from '../../common';
import { disabledShippingDates, storesKind } from '../../../utils/misc';
import CreateShopperNotification from '../../graphql/createShopperNotification';
import UpdateShopperNotification from '../../graphql/updateShopperNotification';
import './shopperNotification.css';

const requiredField = Yup.string().required('form.validations.required');

const ValidationSchema = Yup.object().shape({
    name: requiredField,
    title: requiredField,
    description: requiredField,
    salesChannels: requiredField,
    storeKind: requiredField,
    redirection: Yup.string()
        .matches(/((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
        'Debes ingresar una dirección válida')
        .nullable()
});

const NotificationForm = ({ client, toggle, notification, sessionSalesChannel, email, isCreate, firebaseStorage, initSalesChannels }) => {
    const initialStoreKind = get(find(storesKind, { id: toString(get(notification, 'store_kind')) }), 'name');
    const [loading, setLoading] = useState(false);
    const [requiredReading, setRequiredReading] = useState(get(notification, 'required_reading', false));
    const [stores, setStores] = useState(false);
    const [storeKind, setStoreKind] = useState({ label: initialStoreKind, value: initialStoreKind });
    const [file, setFile] = useState([]);

    useEffect(() => filterStoresByStoreKind(), [storeKind]);
    useEffect(() => {
        async function loadFile () {
            const path = `shopperNotifications/${get(notification, 'id_notification')}`;
            const folderRef = firebaseStorage.ref().child(path);
            const items = await folderRef.list();
            const fileRef = head(get(items, 'items'));

            if (fileRef) {
                const downloadURL = await head(get(items, 'items')).getDownloadURL();
                const fileName = head(get(items, 'items')).name;

                const defaultFile = [
                    {
                        uid: '-1',
                        name: fileName,
                        status: 'done',
                        url: downloadURL
                    }
                ];

                setFile(defaultFile);
            }
        }

        loadFile();
    }, []);

    const filterStoresByStoreKind = () => {
        !isEmpty(get(storeKind, 'value'))
            ? setStores(filter(sessionSalesChannel, { kind: get(storeKind, 'value') }))
            : setStores(sessionSalesChannel);
    };

    const withIdNotification = isCreate ? {} : {
        idNotification: get(notification, 'id_notification'),
        user_update: email
    };

    const getFileRef = (idNotification) => {
        const storageRef = firebaseStorage.ref();
        const path = `shopperNotifications/${idNotification}`;
        const fileName = `Document-${idNotification}`;
        return storageRef.child(`${path}/${fileName}`);
    };

    const uploadToFirebase = async (idNotification) => {
        const fileRef = getFileRef(idNotification);
        const originFile = get(head(file), 'originFileObj');

        fileRef.put(originFile).then(async (snapshot) => {
            const downloadURL = await snapshot.ref.getDownloadURL();

            client.mutate({
                mutation: UpdateShopperNotification,
                variables: { input: { link_file: downloadURL, idNotification } },
                refetchQueries: ['getShopperNotifications']
            }).then(() => console.log('Updated notification with downloadURL'));
        });
    };

    const deleteFromFirebase = (idNotification) => {
        const fileRef = getFileRef(idNotification);
        fileRef.delete().then(() => console.log('Deleted file from firebase'))
        .catch((e) => console.log('Error', e));
    };

    const getVariables = ({ salesChannels, storeKind, destination, shipping, date, ...more }) => {
        const variables = {
            store_kind: toNumber(get(find(storesKind, { name: get(storeKind, 'value') }), 'id')),
            saleschannels: map(salesChannels, (saleChannel) => toNumber(get(saleChannel, 'value'))),
            required_reading: requiredReading ? 1 : 0,
            destiny: toNumber(destination),
            shipping: toNumber(shipping),
            send_date: get(date, 'init').format('YYYY-MM-DD'),
            user_creation: email,
            status: 1,
            ...more
        };

        const isImmediate = (_value, key) => key === 'send_date' && shipping === 1;
        return omitBy(variables, isImmediate);
    };

    const onConfirm = (values) => {
        setLoading(true);
        client.mutate({
            mutation: isCreate ? CreateShopperNotification : UpdateShopperNotification,
            variables: { input: { ...getVariables(values), ...withIdNotification } },
            refetchQueries: ['getShopperNotifications']
        }).then(({ data }) => {
            const idNotification = isCreate
                ? get(data, 'createShopperNotification.idNotificacion')
                : get(notification, 'id_notification');

            isEmpty(file)
                ? deleteFromFirebase(idNotification)
                : uploadToFirebase(idNotification);

            setLoading(false);
            toggle();
        });
    };

    const onChangeStoreKind = (value) => setStoreKind(value);
    const typeShipping = get(notification, 'shipping');
    const initDate = typeShipping === 1 || isEmpty(notification) ? moment() : moment(get(notification, 'send_date'), 'YYYY-MM-DD');

    const initialValues = {
        name: get(notification, 'name'),
        title: get(notification, 'title'),
        description: get(notification, 'description'),
        redirection: get(notification, 'redirection', null),
        salesChannels: initSalesChannels,
        storeKind: get(find(storesKind, { id: toString(get(notification, 'store_kind')) }), 'name'),
        destination: '2',
        shipping: toString(get(notification, 'shipping')),
        date: { init: initDate, end: initDate }
    };

    return (
        <FlexContainer direction='column' flex={1} overflowY style={{ overflowX: 'hidden', maxHeight: 'calc(100% - 115px)' }} mR={0}>
            <FlexContainer direction='column' mR={15}>
                <Formik
                    validationSchema={ValidationSchema}
                    initialValues={initialValues}
                    onSubmit={onConfirm}
                >
                    {({ values }) => {
                        const destination = get(values, 'destination');
                        const shipping = get(values, 'shipping', '');
                        return (<Form className='ant-form'>
                            <FlexContainer direction='column' justify='flex-start' width='100%' mT={10}>
                                <Row width={250}>
                                    <Checkbox
                                        label='Lectura obligatoria'
                                        defaultChecked={requiredReading === 1}
                                        onChange={(value) => setRequiredReading(value)}
                                    />
                                </Row>
                            </FlexContainer>
                            <Field component={FormikInput} label='Nombre' name='name' />
                            <Field component={FormikInput} label='Título' name='title' />
                            <Field component={FormikInput} label='Mensaje' name='description' textArea maxLength={2500} footerNote />
                            <Field component={FormikInput} label='Redirección' name='redirection' />
                            <Field component={FormikSelect} label='Bandera' name='storeKind' options={map(storesKind, ({ name }) => ({ label: name, value: name }))} onChangeStoreKind={(value) => onChangeStoreKind(value)} />
                            <Field multiple component={FormikSelect} label='Tienda' name='salesChannels' options={stores} />
                            <FlexContainer direction='column' justify='flex-start' width='100%' mT={10}>
                                <Text text='Segmentación' strong />
                                <Row width={250}>
                                    <Field component={FormikRadio} disabled label='Todas' name='destination' value={1} checked={destination === '1'} />
                                    <Field component={FormikRadio} label='Mobile' name='destination' value={2} checked={destination === '2'} />
                                    <Field component={FormikRadio} disabled label='Web' name='destination' value={3} checked={destination === '3'} />
                                </Row>
                            </FlexContainer>
                            <FlexContainer direction='column' justify='flex-start' width='100%' mT={10} mB={10}>
                                <Upload
                                    accept='.doc, .docx, .pdf'
                                    multiple={false}
                                    className='upload-button-container-style'
                                    onChange={({ fileList }) => {
                                        const lastFile = isEmpty(fileList) ? [] : [last(fileList)];
                                        setFile(lastFile);
                                    }}
                                    beforeUpload={() => false}
                                    fileList={file}
                                >
                                    <FlexContainer direction='column'>
                                        <Text text='Adjuntar archivo' />
                                        <Button style={{
                                            width: '100%',
                                            borderRadius: 4,
                                            backgroundColor: '#ffffff',
                                            outline: 'none',
                                            border: 'solid 1px #eaeaea',
                                            fontSize: 12
                                        }}>
                                            {`${isEmpty(file) ? 'Seleccionar' : 'Modificar'} archivo`}
                                            <Icon name='clip' color='#4cb358' fontSize={16} left={5} />
                                        </Button>
                                    </FlexContainer>
                                </Upload>
                            </FlexContainer>

                            <FlexContainer direction='column' mT={10}>
                                <Text text='Programación' strong />
                                <Row width={250}>
                                    <Field component={FormikRadio} label='Inmediata' name='shipping' value={1} checked={shipping === '1'} />
                                    <Field component={FormikRadio} label='Programar' name='shipping' value={2} checked={shipping === '2'} />
                                </Row>
                            </FlexContainer>

                            {shipping === '2' && <Field
                                component={FormikDatePicker}
                                label='Fecha'
                                name='date'
                                value={[initDate, initDate]}
                                showDateInput={false}
                                disabledDate={disabledShippingDates}
                                portalStyle={{ position: 'absolute', zIndex: 1, top: '5px' }}
                            />}
                            <ConfirmFooterButtons loading={loading} htmlType='submit' onCancel={toggle} />
                        </Form>);
                    }}
                </Formik>
            </FlexContainer>
        </FlexContainer>
    );
};

NotificationForm.propTypes = {
    client: object,
    toggle: bool,
    notification: object,
    sessionSalesChannel: array,
    email: string,
    isCreate: bool,
    firebaseStorage: object,
    initSalesChannels: array
};

export default withApollo(withFirebase(NotificationForm));
